<script setup lang="ts">
    import { computed, Ref, ref } from 'vue';

    const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    withDefaults(defineProps<{
        show?: boolean;
        enabled?: boolean;
    }>(), {
        show: false,
        enabled: false
    });

    const emits = defineEmits<{
        submit: [args: string[]];
        cancel: [];
    }>();

    const libraryName: Ref<string> = ref('');
    const queryGuid: Ref<string> = ref('');
    const dryRun: Ref<boolean> = ref(true);

    const queryGuidIsValid = computed(() => !queryGuid.value || GUID_PATTERN.test(queryGuid.value.trim()));

    function run() {
        emits('submit', [
            'create-codesets',
            libraryName.value,
            queryGuid.value ? `query=${queryGuid.value.trim()}` : '',
            dryRun.value ? 'dry-run' : ''
        ]);
    }

    function cancelCommand() {
        emits('cancel');
    }
</script>

<template>
    <div v-if="show">
        <h2 class="title is-2 section-header">
            Create code sets for library..
        </h2>
        <p class="block">
            Create code sets for a given Library, one code set per Facility.
            Information from <code>LibraryMetadata</code> and <code>Facility</code> will be used,
            so the Library must have a <code>LibraryMetadata</code> entry.<br>
            Facilities that already have a code set for the Library are skipped.
        </p>
        <p class="block">
            If <code>LibraryMetadata.IsGrouped == false</code> a code set will be created for each valid Facility
            where <code>IsForSTID</code> is true.
        </p>
        <p class="block">
            If <code>LibraryMetadata.IsGrouped == true</code> a code set will be created for each valid Facility
            where <code>IsForSTID</code> is true, but grouped by <code>STIDDB</code>.
            Facilities without <code>STIDDB</code> will not be used.<br>
            THIS SHOULD NOT BE USED if there are existing code sets (command may be improved to support this).
        </p>
        <p class="block">
            Optionally, the target facilities can be selected by a saved query instead. The query must be a
            SQL query returning the ids of the target facilities in a column named <code>ID</code>.
            Any additional column is ignored. When a query is used,
            <code>Facility.IsForSTID</code> is ignored and no grouping is applied,
            so one code set is always created per Facility.<br>
            The command fails if the query does not exist, is not a SQL query, returns no rows,
            has no <code>ID</code> column, or returns an id that does not match a valid Facility.
        </p>
        <div class="block">
            <b-field label="Library name">
                <b-input v-model="libraryName" />
            </b-field>
            <b-field
                label="Saved query GUID (optional)"
                :type="queryGuidIsValid ? '' : 'is-danger'"
                :message="queryGuidIsValid ? '' : 'Must be a GUID, as found in the URL of the saved query'">
                <b-input v-model="queryGuid" />
            </b-field>
            <b-field>
                <b-switch v-model="dryRun">
                    Dry run (rollback transaction)
                </b-switch>
            </b-field>
        </div>
        <div class="buttons">
            <b-button
                type="is-primary"
                :disabled="!enabled || !queryGuidIsValid"
                @click="run()">
                Run
            </b-button>
            <b-button
                :disabled="!enabled"
                @click="cancelCommand()">
                Cancel
            </b-button>
        </div>
    </div>
</template>
